import type { User, LoginCredentials, AuthResponse } from "@unreserved/types";
import { API_ENDPOINTS, COOKIE_NAMES, LOCAL_STORAGE_KEYS } from "@unreserved/constants";
import { apiService } from "./api.service";
import { storageService } from "./storage.service";

/**
 * Authentication Service
 */
class AuthService {
  /**
   * Login user
   */
  async login(email: string, password: string): Promise<AuthResponse | null> {
    const credentials: LoginCredentials = { email, password };
    
    const response = await apiService.post<AuthResponse>(
      API_ENDPOINTS.AUTH_LOGIN,
      credentials
    );

    if (response.success && response.data) {
      this.setAuthData(response.data);
      return response.data;
    }

    return null;
  }

  /**
   * Logout user
   */
  async logout(deviceId?: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH_LOGOUT, deviceId ? { deviceId } : undefined);
    this.clearAuthData();
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    const userJson = storageService.get(LOCAL_STORAGE_KEYS.USER);
    if (!userJson) return null;
    
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get auth token
   */
  getAuthToken(): string | null {
    return storageService.get(COOKIE_NAMES.AUTH_TOKEN);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Set authentication data
   */
  private setAuthData(authData: AuthResponse): void {
    storageService.set(COOKIE_NAMES.AUTH_TOKEN, authData.token);
    storageService.set(COOKIE_NAMES.REFRESH_TOKEN, authData.refreshToken);
    storageService.set(LOCAL_STORAGE_KEYS.USER, JSON.stringify(authData.user));
    
    apiService.setAuthToken(authData.token);
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    storageService.remove(COOKIE_NAMES.AUTH_TOKEN);
    storageService.remove(COOKIE_NAMES.REFRESH_TOKEN);
    storageService.remove(LOCAL_STORAGE_KEYS.USER);
    
    apiService.removeAuthToken();
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<boolean> {
    const refreshToken = storageService.get(COOKIE_NAMES.REFRESH_TOKEN);
    if (!refreshToken) return false;

    const response = await apiService.post<AuthResponse>(
      API_ENDPOINTS.AUTH_REFRESH,
      { refreshToken }
    );

    if (response.success && response.data) {
      this.setAuthData(response.data);
      return true;
    }

    this.clearAuthData();
    return false;
  }
}

export const authService = new AuthService();

